'use client'

import { createContext, ReactNode, useCallback, useContext, useEffect, useRef, useState } from 'react'

import { TProductReviewsSummary } from '@/shared/types/product-types'

import { useLazyGetProductReviewRatingsQuery } from '../api/product-reviews-api'

type Rating = TProductReviewsSummary | null
type RatingsContext = {
	ratings: Record<string, Rating>
	register: (code: string) => void
}

const CardReviewRatingsContext = createContext<RatingsContext | null>(null)

export const CardReviewRatingsProvider = ({ children }: { children: ReactNode }) => {
	const [getRatings] = useLazyGetProductReviewRatingsQuery()
	const [ratings, setRatings] = useState<Record<string, Rating>>({})
	const pending = useRef(new Set<number>())
	const requested = useRef(new Set<number>())
	const timer = useRef<ReturnType<typeof setTimeout> | null>(null)

	const flush = useCallback(async () => {
		timer.current = null
		const codes = [...pending.current]
		pending.current.clear()
		if (!codes.length) return
		codes.forEach(code => requested.current.add(code))

		try {
			const result = await getRatings(codes).unwrap()
			setRatings(previous => {
				const next = { ...previous }
				codes.forEach(code => {
					const rating = result[String(code)]
					next[code] = { RATING: rating?.rating ?? 0, COUNT: rating?.count ?? 0 }
				})
				return next
			})
		} catch {
			setRatings(previous => {
				const next = { ...previous }
				codes.forEach(code => {
					next[code] = null
				})
				return next
			})
		}
	}, [getRatings])

	const register = useCallback(
		(code: string) => {
			const normalizedCode = Number(code)
			if (!Number.isFinite(normalizedCode) || requested.current.has(normalizedCode)) return
			pending.current.add(normalizedCode)
			if (timer.current === null) timer.current = setTimeout(flush, 0)
		},
		[flush]
	)

	useEffect(
		() => () => {
			if (timer.current !== null) clearTimeout(timer.current)
			timer.current = null
			pending.current.clear()
		},
		[]
	)

	return <CardReviewRatingsContext.Provider value={{ ratings, register }}>{children}</CardReviewRatingsContext.Provider>
}

export const useCardReviewRating = (code: string, summary?: TProductReviewsSummary | null): Rating => {
	const context = useContext(CardReviewRatingsContext)
	const register = context?.register

	useEffect(() => {
		if (summary === undefined && code) register?.(code)
	}, [code, register, summary])

	return summary === undefined ? context?.ratings[code] ?? null : summary
}
